import { currentFiscalYear, fiscalYearLabel } from "../datetime.ts";
import { TAX_SENSITIVITY } from "../review-due.ts";
import { COMMON_RULES } from "./common.ts";
import type { ProfessionPack } from "./types.ts";

export const TAX_ACCOUNTANT: ProfessionPack = {
  id: "tax_accountant",
  label: "税理士",
  supervisorTitle: "税理士",
  registrationLabel: "税理士登録番号",
  reviewer: "税理士の先生",
  writingRules: [
    "依頼主の税理士事務所の立場で書く。文体は「です・ます」。一人称と表記は事務所の設定に従う。",
    "断定してよいのは、法令・国税庁の資料などの公的な一次資料に根拠がある場合だけ。根拠とした資料を出典として示す。",
    "税制は年度を明記する（例：「2026年度（令和8年度）の税制では」）。改正の予定は「予定」と書く。",
    "税率・控除額・期限などの数字は、一次資料で確認できたものだけを書く。確認できない数字は書かない。",
    "誇大な表現、成果の保証（「必ず節税できる」「絶対」など）、根拠のない比較や最上級（「地域No.1」など）は使わない。",
    "個別の相談に当たる断定的な助言はしない。個別の判断が必要な内容は「ご相談ください」とし、CTA につなげる。",
    "他のサイトの文章を写さない。競合の情報は構成の参考と、足りない点の把握にだけ使う。",
    "検索した人の知りたいことに冒頭で答える。必要に応じて FAQ を入れる。",
    "文字数稼ぎの水増しをしない。",
  ],
  expressionRules: [
    ...COMMON_RULES,
    {
      id: "tax-guarantee",
      pattern: /(必ず|確実に|絶対に?)(節税|還付|税金が(安く|戻)|得)/g,
      severity: "high",
      category: "節税効果の保証",
      message: "節税・還付の効果を保証する表現は使えません",
      suggestion: "「要件を満たせば〜できる場合があります」とし、要件を説明する",
    },
    {
      id: "tax-evasion",
      pattern: /バレない|税務署に(見つから|ばれ)|脱税/g,
      severity: "high",
      category: "不適切な内容",
      message: "租税回避・脱税を想起させる表現は、税理士の記事として不適切です",
      suggestion: "適法な制度の活用として説明する",
    },
    {
      id: "tax-individual",
      pattern: /あなたは.{0,20}(すべき|しなければなりません|した方が得)/g,
      severity: "medium",
      category: "個別の助言",
      message: "個別の事情によらず断定する助言になっています",
      suggestion: "「〜のような場合は、〜を検討するとよいでしょう。詳しくはご相談ください」にする",
    },
    {
      id: "tax-future",
      pattern: /改正(され|になり)ます|(施行|適用)されます/g,
      severity: "low",
      category: "改正の書き方",
      message: "改正前の内容なら「予定」と明記する必要があります",
      suggestion: "「〜年度から〜となる予定です（〇年〇月時点）」と書く",
    },
  ],
  trustedDomains: ["nta.go.jp", "laws.e-gov.go.jp", "elaws.e-gov.go.jp", "mof.go.jp", "eltax.lta.go.jp", "soumu.go.jp", "nenkin.go.jp", "mhlw.go.jp", "chusho.meti.go.jp"],
  authorityDomains: [
    "freee.co.jp",
    "biz.moneyforward.com",
    "moneyforward.com",
    "yayoi-kk.co.jp",
    "zeiri4.com",
    "tkc.jp",
    "smbc.co.jp",
    "bk.mufg.jp",
    "mizuhobank.co.jp",
    "nikkei.com",
    "ja.wikipedia.org",
    "souzoku.asahi.com",
  ],
  specialtyOptions: ["法人税", "所得税・確定申告", "相続税・贈与税", "事業承継", "消費税・インボイス", "会社設立・創業支援", "医業経営", "資産税・不動産", "国際税務", "税務調査対応", "経営計画・資金繰り", "記帳代行・クラウド会計"],
  // 税務の暦: statutory periods, plus the family gatherings when 相続 is talked over.
  seasons: [
    { id: "hotei-chosho", name: "償却資産の申告・法定調書", start: { month: 1, day: 1 }, end: { month: 1, day: 31 }, leadWeeks: 6, terms: ["償却資産", "法定調書", "給与支払報告書"], note: "1月末が提出期限です。年末までに公開しておくと、事業者の検索に間に合います。" },
    { id: "zoyo-shinkoku", name: "贈与税の申告", start: { month: 2, day: 1 }, end: { month: 3, day: 15 }, leadWeeks: 8, terms: ["贈与税", "住宅取得資金", "相続時精算課税"], note: "前年に受けた贈与の申告期間です。12月から準備の検索が増えます。" },
    { id: "kakutei-shinkoku", name: "確定申告", start: { month: 2, day: 16 }, end: { month: 3, day: 15 }, leadWeeks: 8, terms: ["確定申告", "医療費控除", "ふるさと納税", "副業", "青色申告", "還付", "雑所得"], note: "1年でいちばん検索が増える時期です。1月中に公開しておくと、検索が増える時期に間に合います。" },
    { id: "shohizei-kojin", name: "個人事業者の消費税の申告", start: { month: 3, day: 1 }, end: { month: 3, day: 31 }, leadWeeks: 8, terms: ["消費税", "インボイス", "簡易課税", "2割特例"], note: "3月末が期限です。インボイスに関わる事業者の検索が増えます。" },
    { id: "hojin-kessan", name: "3月決算の法人の申告", start: { month: 4, day: 1 }, end: { month: 5, day: 31 }, leadWeeks: 6, terms: ["法人税", "決算", "法人 申告", "役員報酬"], note: "いちばん多い3月決算の法人が、5月末までに申告します。" },
    { id: "kotei-shisanzei", name: "固定資産税・自動車税の通知", start: { month: 4, day: 1 }, end: { month: 5, day: 31 }, leadWeeks: 6, terms: ["固定資産税", "自動車税"], note: "通知が届いて、金額や減免を調べる人が増えます。" },
    { id: "juminzei", name: "住民税の決定通知", start: { month: 6, day: 1 }, end: { month: 6, day: 30 }, leadWeeks: 6, terms: ["住民税"], note: "通知が届き、金額の見方や増えた理由を調べる人が増えます。" },
    { id: "rosenka", name: "路線価の公表", start: { month: 7, day: 1 }, end: { month: 7, day: 31 }, leadWeeks: 6, terms: ["路線価", "土地 評価", "小規模宅地", "相続税 評価"], note: "7月1日に国税庁が路線価を公表し、土地の相続税評価を調べる人が増えます。" },
    { id: "obon", name: "お盆の帰省（相続の話し合い）", start: { month: 8, day: 1 }, end: { month: 8, day: 20 }, leadWeeks: 6, terms: ["相続", "生前贈与", "遺言", "家族信託", "遺産分割"], note: "家族が集まり、相続や生前贈与の話題が出やすい時期です。" },
    { id: "nenmatsu-chosei", name: "年末調整", start: { month: 10, day: 15 }, end: { month: 12, day: 31 }, leadWeeks: 8, terms: ["年末調整", "保険料控除", "扶養控除", "源泉徴収"], note: "10月に控除証明書が届き、年末調整の検索が増えます。" },
    { id: "rekinen-zoyo", name: "年内の贈与・節税", start: { month: 11, day: 1 }, end: { month: 12, day: 31 }, leadWeeks: 8, terms: ["暦年贈与", "生前贈与", "贈与", "節税", "ふるさと納税"], note: "暦年贈与やふるさと納税は12月31日が区切りです。年内に動きたい人が調べます。" },
    { id: "nenmatsu-nenshi", name: "年末年始の帰省（相続の話し合い）", start: { month: 12, day: 25 }, end: { month: 1, day: 5 }, leadWeeks: 6, terms: ["相続", "遺言", "生前贈与", "相続税"], note: "家族が集まる年末年始は、相続の話題が出やすい時期です。" },
  ],
  officeNoun: "税理士事務所",
  officeExample: "山田税理士事務所",
  affiliationExample: "近畿税理士会",
  sourcesPhrase: "国税庁などの公的な資料",
  sourceExamples: "国税庁・e-Gov 法令検索",
  keyFigures: "税率・控除額・期限",
  publishers: {
    "nta.go.jp": "国税庁",
    "e-gov.go.jp": "e-Gov 法令検索",
    "mof.go.jp": "財務省",
    "eltax.lta.go.jp": "地方税共同機構（eLTAX）",
    "soumu.go.jp": "総務省",
    "mhlw.go.jp": "厚生労働省",
    "nenkin.go.jp": "日本年金機構",
    "chusho.meti.go.jp": "中小企業庁",
  },
  period: {
    label: fiscalYearLabel,
    short: (y) => `${y}年度`,
    rules: (y) => `${fiscalYearLabel(y)}の制度`,
    brief: (y) => `対象とする税制の年度：${fiscalYearLabel(y)}（数字や制度はこの年度のものを書き、年度を明記する）`,
    mustState: true,
    current: currentFiscalYear,
  },
  review: { name: "年度の見直し", when: "新しい年度（4月）や税制改正のあと", nowLabel: "今年度", currentArticle: "今年度の記事", updateLabel: "年度の更新", sensitivity: TAX_SENSITIVITY },
  features: { articles: true },
};
